// Aan welk onderzoek (of welke vragenlijst) hangt een indicator?
//
// Nodig omdat het TOC-detail van een effect dat per indicator toont
// (updateverzoek Broodje Aap & Linke Soep, inbox #328) en geen bestaande
// helper die vraag per vraag beantwoordt: resolveResearchIdsForEffect werkt per
// effect en geeft ID's, geen namen.
//
// De koppeling loopt QuestionnaireFields.QuestionnaireID →
// Research.QuestionnaireID (ADR-016: één vragenlijst per onderzoek, maar één
// vragenlijst kan in meer onderzoeken zitten).

/**
 * @typedef {object} IndicatorLink
 * @property {'research' | 'questionnaire' | 'none'} kind - Waar de indicator aan hangt
 * @property {string} label - Korte tekst voor in de regel
 * @property {string[]} names - Alle namen voluit, voor het uitgeklapte detail
 */

/**
 * @param {{QuestionnaireID?: string}} field - Het QuestionnaireFields-document
 * @param {Array<{QuestionnaireID?: string, Title?: string, Position?: number}> | undefined} researches - Levende onderzoeken; undefined = nog niet geladen
 * @param {Array<{ID: string, Title?: string}> | undefined} questionnaires - Levende vragenlijsten; undefined = nog niet geladen
 * @returns {IndicatorLink | null} null zolang het nog niet te zeggen is
 */
const indicatorLink = (field, researches, questionnaires) => {
    const questionnaireId = field?.QuestionnaireID || ''
    if (!questionnaireId) return { kind: 'none', label: 'Niet in een vragenlijst', names: [] }
    if (!Array.isArray(researches)) return null

    const names = researches
        .filter((r) => r.QuestionnaireID === questionnaireId)
        .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
        .map((r) => r.Title || 'Naamloos onderzoek')

    if (names.length > 0) {
        const extra = names.length - 1
        // "Onderzoek 'Onderzoek Gelijke Kansen'" leest dubbel: begint de naam
        // al met het woord, dan geen voorvoegsel.
        const prefix = /^onderzoek\b/i.test(names[0]) ? '' : 'Onderzoek '
        return {
            kind: 'research',
            label: `${prefix}'${names[0]}'${extra > 0 ? ` + ${extra}` : ''}`,
            names,
        }
    }

    if (!Array.isArray(questionnaires)) return null
    const questionnaire = questionnaires.find((q) => q.ID === questionnaireId)
    const title = questionnaire?.Title || 'Naamloze vragenlijst'
    return { kind: 'questionnaire', label: `Vragenlijst '${title}', geen onderzoek`, names: [title] }
}

export default indicatorLink
